import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { importAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  Download, 
  CheckCircle2, 
  AlertCircle,
  ArrowLeft,
  Calendar,
  ShoppingBag,
  Package,
  DollarSign,
  Store
} from 'lucide-react';

const ENTITY_TYPES = [
  { id: 'events', label: 'Events', icon: Calendar, color: 'blue' },
  { id: 'products', label: 'Products', icon: ShoppingBag, color: 'purple' },
  { id: 'materials', label: 'Materials', icon: Package, color: 'amber' },
  { id: 'sales', label: 'Sales', icon: DollarSign, color: 'green' },
  { id: 'boutiques', label: 'Boutiques', icon: Store, color: 'rose' },
];

export const BulkImport = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [entityType, setEntityType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Admin-only feature. Regular users see a clear explainer and a link home.
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center px-6" data-testid="bulk-import-admin-gate">
        <div className="max-w-lg text-center space-y-4 bg-white border-2 border-amber-200 rounded-2xl p-8 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F]">Admins only</h1>
          <p className="text-[#4A4A4A]">
            Bulk import is reserved for AMSCompass admins. Imported events become
            shared community events for everyone to discover.
          </p>
          <p className="text-sm text-[#71717A]">
            Have a CSV of events you&rsquo;d like added to the community? Email{' '}
            <a href="mailto:support@amscompass.com" className="text-[#FE5F55] font-semibold underline">
              support@amscompass.com
            </a>{' '}
            and we&rsquo;ll review and import them for you.
          </p>
          <Button
            onClick={() => navigate('/events')}
            className="btn-primary"
            data-testid="bulk-import-back-btn"
          >
            Back to My Events
          </Button>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      return;
    }
    
    if (selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error('Please select a CSV file');
      // Reset the input
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    if (!entityType) {
      toast.error('Please select an entity type first');
      return;
    }
    
    try {
      const response = await importAPI.getTemplate(entityType);
      
      // The response is raw CSV text (since it's a StreamingResponse)
      const csvContent = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      
      // Create and download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}_template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!entityType) {
      toast.error('Please select an entity type');
      return;
    }
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploading(true);
    setResult(null);
    
    try {
      let response;
      switch (entityType) {
        case 'events':
          response = await importAPI.importEvents(file);
          break;
        case 'products':
          response = await importAPI.importProducts(file);
          break;
        case 'materials':
          response = await importAPI.importMaterials(file);
          break;
        case 'sales':
          response = await importAPI.importSales(file);
          break;
        case 'boutiques':
          response = await importAPI.importBoutiques(file);
          break;
        default:
          throw new Error('Invalid entity type');
      }
      
      setResult(response.data);
      
      const importedCount = response.data.imported_count || 0;
      if (importedCount > 0) {
        toast.success(`Successfully imported ${importedCount} records!`);
      }
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors encountered`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const selectedEntity = ENTITY_TYPES.find(e => e.id === entityType);

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Bulk Import</h1>
            <p className="text-gray-600 mt-1">Import data from CSV files</p>
          </div>
        </div>

        {/* Admin-imported events become community events — make this explicit */}
        {entityType === 'events' && (
          <div
            className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl"
            data-testid="bulk-import-community-notice"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Imported events go to the community directory.</p>
              <p className="text-amber-800/90 mt-0.5">
                Every event in this CSV will be saved as a <strong>community event</strong> — discoverable
                by all AMSCompass users. They won&rsquo;t be assigned to your personal <em>My Events</em> list.
                Only admins can edit or delete them later.
              </p>
            </div>
          </div>
        )}

        {/* Main Import Card */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
          {/* Step 1: Select Entity Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
              1. Select Data Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {ENTITY_TYPES.map((entity) => (
                <button
                  key={entity.id}
                  onClick={() => { setEntityType(entity.id); setResult(null); }}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    entityType === entity.id
                      ? 'border-[#1E3A5F] bg-[#FFEDDF]'
                      : 'border-gray-200 hover:border-[#1E3A5F]/50'
                  }`}
                  data-testid={`entity-${entity.id}`}
                >
                  <entity.icon className={`w-6 h-6 mx-auto mb-2 ${
                    entityType === entity.id ? 'text-[#1E3A5F]' : 'text-gray-400'
                  }`} />
                  <span className={`text-sm font-medium ${
                    entityType === entity.id ? 'text-[#1E3A5F]' : 'text-gray-600'
                  }`}>
                    {entity.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Download Template */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
              2. Download CSV Template
            </label>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={!entityType}
              className="w-full border-2 border-[#1E3A5F] text-[#1E3A5F]"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download {selectedEntity?.label || 'Template'}
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Download the template, fill in your data, and upload it below
            </p>
          </div>

          {/* Step 3: Upload File */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-[#1E3A5F] mb-2">
              3. Upload Your CSV File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1E3A5F]/50 transition-colors">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                {file ? (
                  <div>
                    <p className="font-medium text-[#1E3A5F]">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-gray-600">Click to select CSV file</p>
                    <p className="text-sm text-gray-400">or drag and drop</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Import Button */}
          <Button
            onClick={handleImport}
            disabled={!entityType || !file || uploading}
            className="w-full btn-primary"
            data-testid="import-btn"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Import {selectedEntity?.label || 'Data'}
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="mt-6 bg-white rounded-xl border-2 border-[#1E3A5F] p-6 shadow-hard">
            <h2 className="text-lg font-semibold text-[#1E3A5F] mb-4">Import Results</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className={`p-4 rounded-lg ${(result.imported_count || 0) > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-5 h-5 ${(result.imported_count || 0) > 0 ? 'text-green-500' : 'text-gray-400'}`} />
                  <span className="font-semibold text-green-700">Successful</span>
                </div>
                <p className="text-2xl font-bold text-green-600 mt-1">{result.imported_count || 0}</p>
              </div>
              
              <div className={`p-4 rounded-lg ${(result.errors?.length || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle className={`w-5 h-5 ${(result.errors?.length || 0) > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                  <span className="font-semibold text-red-700">Errors</span>
                </div>
                <p className="text-2xl font-bold text-red-600 mt-1">{result.errors?.length || 0}</p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Error Details:</h3>
                <div className="bg-red-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="space-y-1 text-sm text-red-700">
                    {result.errors.map((error, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {(result.imported_count || 0) > 0 && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  Successfully imported {result.imported_count} {selectedEntity?.label.toLowerCase() || 'records'}!
                  {entityType === 'events' && ' View them in My Events.'}
                  {entityType === 'products' && ' View them in Products.'}
                  {entityType === 'materials' && ' View them in Materials.'}
                  {entityType === 'sales' && ' View them in Sales.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 bg-[#FFEDDF] rounded-xl border-2 border-[#1E3A5F] p-6">
          <h3 className="font-semibold text-[#1E3A5F] mb-3">Import Tips</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">1.</span>
              Download the template for the correct column headers
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">2.</span>
              Keep dates in YYYY-MM-DD format (e.g., 2026-04-15)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">3.</span>
              For sales import, product names must match existing products exactly
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">4.</span>
              Event types: craft_show, festival, comic_con, art_show, home_show, flea_market, farmers_market, holiday_market, other
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">5.</span>
              Event start_time / end_time use 24-hour HH:MM format (e.g., 09:00, 17:30). Leave blank if unknown.
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#1E3A5F]">6.</span>
              Boutique commission_rate is a percentage (e.g., 40 = 40%).
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
